import { type ChangeEventHandler, type FormEvent, useState } from 'react'
import { useCookies } from 'react-cookie'
import styled from 'styled-components'

import { FREEGUIDE_EMAIL_SUBMITTED } from '@/lib/braze/brazeConstants'
import { registerBrazeUserWithEmailFromWebForm } from '@/lib/braze/brazeUtils'
import { QUIZ_ID_COOKIE_NAME } from '@/lib/checkoutConstants'
import { trackSignupEvent } from '@/lib/gtm/gtmV2'
import { useCheckoutCountry } from '@/lib/hooks/useCheckoutCountry'

const StyledContainer = styled.div`
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  margin: 0 auto -180px;
  max-width: 800px;
  padding: 32px;
  box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.1);

  h3 {
    color: ${(p) => p.theme.v2.onSurface.text};
    font-family: ${(p) => p.theme.v2.font.nantes};
    line-height: ${(p) => p.theme.v2.typography.body.xl.lineHeight};
    font-size: ${(p) => p.theme.v2.typography.body.xl.fontSize};
    margin: 0 0 12px 0;
  }

  span,
  p {
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueRegular};
    font-size: ${(p) => p.theme.v2.typography.body.xxs.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.xxs.lineHeight};

    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      margin: 0 auto 24px;
    }
  }

  p.error {
    color: #eb7159;
    margin: 24px 0 0;
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    margin: 0 24px;
    text-align: center;
  }
`

const StyledForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`

const StyledField = styled.div`
  flex: 0 0 calc(50% - 12px);
  margin: 24px 0 0;

  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    flex: 0 0 100%;
    gap: 16px;
    margin: 0 0 16px;
  }

  label {
    display: block;
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    font-size: ${(p) => p.theme.v2.typography.body.xxs.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.xxs.lineHeight};
    margin-bottom: 5px;
    text-align: left;
  }

  input {
    border: 1px solid ${(p) => p.theme.colours.separatorBorder};
    border-radius: 4px;
    height: 48px;
    padding: 0 16px;
    width: 100%;
  }
`
const StyledButton = styled.div`
  margin-top: 32px;
  input,
  a {
    appearance: none;
    background: ${(p) => p.theme.v2.button.dark.bgColor};
    border: none;
    border-radius: 36px;
    color: ${(p) => p.theme.v2.button.dark.textColor};
    cursor: pointer;
    padding: 16px 32px;

    &:hover {
      background: ${(p) => p.theme.v2.button.dark.hoverColor};
    }

    &:disabled {
      background: ${(p) => p.theme.v2.button.dark.disabledColor};
      cursor: default;
    }

    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    width: 100%;
    margin-top: 0;
  }
`

export const FreeGuideSignup = () => {
  const [errorMessage, setErrorMessage] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [header, setHeader] = useState('Sign up to our newsletter to get your free guide')
  const [tagline, setTagline] = useState('Join our newsletter. No spam. Just science.')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [cookies] = useCookies([QUIZ_ID_COOKIE_NAME])
  const { isReady, geoLocCountry } = useCheckoutCountry()

  const handleNameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newName = event.target.value
    setName(newName)
    validateForm(newName, email)
  }

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newEmail = event.target.value
    setEmail(newEmail)
    validateForm(name, newEmail)
  }

  const validateForm = (newName: string, newEmail: string) => {
    const isValidForm = newName.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
    setIsValid(isValidForm)
  }

  const subscribeCallback = (success: boolean, message?: string) => {
    setIsSubscribed(success)

    if (success) {
      setHeader('Please check your email for a link to download your guide')
      setTagline('We hope these learnings are going to help you live a healthier life.')
    } else {
      if (message) setErrorMessage(message)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    setErrorMessage('')

    if (email) {
      const marketingConsent = true
      const additionalFields = {
        ...(name && { firstName: name }),
        ...(cookies.cUserId && { quizUserId: cookies.cUserId }),
        ...(isReady && geoLocCountry && { country: geoLocCountry }),
      }
      await registerBrazeUserWithEmailFromWebForm(
        e,
        email,
        FREEGUIDE_EMAIL_SUBMITTED,
        subscribeCallback,
        marketingConsent,
        Object.keys(additionalFields).length !== 0 ? additionalFields : undefined,
      )
    }

    trackSignupEvent({
      email: email,
      section: 'free_guide',
      emailList: 'newsletter',
    })
  }

  return (
    <StyledContainer>
      <h3>{header}</h3>
      <span>{tagline}</span>
      {!isSubscribed && (
        <StyledForm method="post" onSubmit={handleSubmit}>
          <StyledField>
            <label htmlFor="name">Your first name</label>
            <input id="name-input" name="name" required type="text" value={name} onChange={handleNameChange} />
          </StyledField>
          <StyledField>
            <label htmlFor="name">Your email</label>
            <input id="email-input" name="email" required type="email" value={email} onChange={handleEmailChange} />
          </StyledField>
          <StyledButton>
            <input type="submit" value="Send me the guide" disabled={!isValid} />
          </StyledButton>
        </StyledForm>
      )}
      {errorMessage && <p className={isSubscribed ? 'success' : 'error'}>{errorMessage}</p>}
      {isSubscribed && (
        <StyledButton>
          <a href="https://zoe.com">Check out ZOE</a>
        </StyledButton>
      )}
    </StyledContainer>
  )
}
